import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { queryClient } from './queryClient';

// Every list the app shows is described once, here. Pages ask for it by name,
// so two pages that need the same data share one request and one cache entry.
const read = (url) => async ({ signal }) => (await api.get(url, { signal })).data;

export const queryDefs = {
  events: { queryKey: ['events'], queryFn: read('/events') },
  institutions: { queryKey: ['institutions'], queryFn: read('/institutions') },
  tasks: { queryKey: ['tasks'], queryFn: read('/tasks') },
  teamMembers: { queryKey: ['team-members'], queryFn: read('/team-members') },
  users: { queryKey: ['users'], queryFn: read('/users') },
  dashboardStats: { queryKey: ['dashboard-stats'], queryFn: read('/dashboard/stats') },
  notifications: {
    queryKey: ['notifications', 'list'],
    queryFn: async ({ signal }) => (await api.get('/notifications', { signal })).data.slice(0, 10),
  },
  unreadCount: {
    queryKey: ['notifications', 'unread'],
    queryFn: async ({ signal }) => (await api.get('/notifications/unread-count', { signal })).data.count,
  },
};

export const useEvents = (options) => useQuery({ ...queryDefs.events, ...options });
export const useInstitutions = (options) => useQuery({ ...queryDefs.institutions, ...options });
export const useTasks = (options) => useQuery({ ...queryDefs.tasks, ...options });
export const useTeamMembers = (options) => useQuery({ ...queryDefs.teamMembers, ...options });
export const useUsers = (options) => useQuery({ ...queryDefs.users, ...options });
export const useDashboardStats = (options) => useQuery({ ...queryDefs.dashboardStats, ...options });
export const useNotifications = (options) => useQuery({ ...queryDefs.notifications, ...options });
export const useUnreadCount = (options) => useQuery({ ...queryDefs.unreadCount, ...options });

// What each screen needs, so we can warm the cache before the click lands.
const ROUTE_DATA = {
  '/dashboard': ['dashboardStats', 'events'],
  '/events': ['events', 'institutions'],
  '/institutions': ['institutions', 'events'],
  '/tasks': ['tasks', 'teamMembers', 'events'],
  '/employees': ['teamMembers', 'tasks'],
  '/users': ['users'],
  '/my-tasks': ['tasks'],
  '/calendar': ['events'],
  '/my-calendar': ['tasks'],
};

// Fire-and-forget: only fetches when the data is missing or stale, and a
// failure (for example a role without access) is silently ignored.
export const prefetchRoute = (path) => {
  (ROUTE_DATA[path] || []).forEach((name) => {
    queryClient.prefetchQuery(queryDefs[name]).catch(() => {});
  });
};
